/**
 * /admin/ai-usage — per-feature revenue / cost / margin / accept-rate
 * with B2C / B2B / Owner segment tabs.
 *
 * Wallet txs are bucketed by the user that owns the wallet:
 *   • Owner  — is_superuser=True (founder-freebies, no revenue but real cost)
 *   • B2B    — non-superuser, has organization_id (the paying business plans)
 *   • B2C    — non-superuser, no organization_id (the solo scorers)
 *   • All    — everything in one bucket (reference total)
 *
 * Each tab carries its own per-feature breakdown + totals so swapping
 * tabs is instant (no re-fetch).
 *
 * URL-backed: ?range=7|30|90 controls the window. Default 30 days.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { getAIUsageDashboard } from "../../api/admin.extras.api";
import { toArray } from "../../utils/toArray";

const SEGMENTS = [
  { key: "all",   label: "All" },
  { key: "owner", label: "Owner" },
  { key: "b2b",   label: "B2B" },
  { key: "b2c",   label: "B2C" },
];

const RANGES = [
  { value: 7,  label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];
const RANGE_VALUES = new Set(RANGES.map((r) => r.value));

const EMPTY_BUCKET = {
  features: [],
  totals: { calls: 0, revenue_inr: 0, cost_inr_est: 0, margin_pct: null },
};

export default function AdminAIUsage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawRange = parseInt(searchParams.get("range") || "30", 10);
  const days = RANGE_VALUES.has(rawRange) ? rawRange : 30;

  const [segments, setSegments] = useState({});
  const [usdInr, setUsdInr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSeg, setActiveSeg] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getAIUsageDashboard({ days });
      setSegments(r.data.segments || {});
      setUsdInr(r.data.usd_inr ?? null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load AI usage.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  function setRange(next) {
    const sp = new URLSearchParams(searchParams);
    sp.set("range", String(next));
    setSearchParams(sp, { replace: true });
  }

  const bucket = segments[activeSeg] || EMPTY_BUCKET;
  // Defensive — backend could omit either field on an empty segment.
  const features = toArray(bucket.features);
  const totals = bucket.totals || EMPTY_BUCKET.totals;

  const segCounts = useMemo(() => {
    const out = {};
    for (const s of SEGMENTS) {
      out[s.key] = segments[s.key]?.totals?.calls ?? 0;
    }
    return out;
  }, [segments]);

  const rangeLabel = days === 7 ? "7 days" : days === 90 ? "90 days" : "30 days";

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">AI Usage (last {rangeLabel})</h1>
          <p className="text-sm text-text-secondary mt-1">
            Revenue vs cost per feature. Margin under 80% deserves a pricing review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Time range selector */}
          <div
            role="radiogroup"
            aria-label="Time range"
            className="inline-flex items-center rounded-md border border-border-default overflow-hidden text-sm"
          >
            {RANGES.map((r) => {
              const active = days === r.value;
              return (
                <button
                  key={r.value}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setRange(r.value)}
                  className={`px-3 py-1.5 transition-colors ${
                    active
                      ? "bg-button-primary text-white"
                      : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center justify-between gap-2"
        >
          <span className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="truncate">{error}</span>
          </span>
          <button
            onClick={load}
            disabled={loading}
            className="shrink-0 text-xs px-2 py-1 rounded border border-red-500/60 hover:bg-red-500/20 disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      )}

      {/* Segment tabs */}
      <div className="flex items-center gap-1 border-b border-border-default overflow-x-auto">
        {SEGMENTS.map((s) => {
          const isActive = activeSeg === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setActiveSeg(s.key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                isActive
                  ? "border-violet-500 text-text-primary font-semibold"
                  : "border-transparent text-text-secondary hover:text-text-primary"
              }`}
            >
              {s.label}
              <span className="ml-1.5 text-[10px] tabular-nums text-text-secondary">
                {segCounts[s.key].toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Per-segment totals strip — responsive so phones don't overflow */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <KpiCard label="Calls" value={(totals.calls ?? 0).toLocaleString()} />
        <KpiCard label="Revenue" value={`₹${(totals.revenue_inr ?? 0).toLocaleString()}`} />
        <KpiCard label="Cost (est)" value={`₹${(totals.cost_inr_est ?? 0).toLocaleString()}`} />
        <KpiCard
          label="Net margin"
          value={totals.margin_pct == null ? "—" : `${totals.margin_pct}%`}
          color={marginColor(totals.margin_pct)}
        />
      </div>

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">
            Per-feature AI usage — {SEGMENTS.find((s) => s.key === activeSeg)?.label} segment, last {rangeLabel}
          </caption>
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th scope="col" className="px-4 py-2.5">Feature</th>
              <th scope="col" className="px-4 py-2.5 text-right">Calls</th>
              <th scope="col" className="px-4 py-2.5 text-right">Revenue ₹</th>
              <th scope="col" className="px-4 py-2.5 text-right">Cost ₹ (est)</th>
              <th scope="col" className="px-4 py-2.5 text-right">Margin %</th>
              <th scope="col" className="px-4 py-2.5 text-right">Accept rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && features.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && features.length === 0 && !error && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary">
                No AI calls in this segment over the last {rangeLabel}.
              </td></tr>
            )}
            {features.map((f) => (
              <tr key={f.code} className="hover:bg-bg-secondary">
                <td className="px-4 py-2 font-mono text-xs">{f.code}</td>
                <td className="px-4 py-2 text-right tabular-nums">{f.calls}</td>
                <td className="px-4 py-2 text-right tabular-nums">₹{f.revenue_inr}</td>
                <td className="px-4 py-2 text-right tabular-nums">₹{f.cost_inr_est}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">
                  <PctPill value={f.margin_pct} colorFn={marginColor} />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <PctPill value={f.accept_rate} colorFn={acceptColor} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-text-secondary">
        Segments: <strong>Owner</strong> = superuser (founder freebies, no revenue
        but real cost). <strong>B2B</strong> = users with an organization.
        <strong> B2C</strong> = solo accounts. Accept rate source:{" "}
        <code>AIObservation.was_corrected</code>. Margin source:{" "}
        <code>WalletTransaction.cost_usd</code> vs <code>delta_credits</code>.
        Reversals (auto-refunds) excluded. USD→INR ={" "}
        <code>{usdInr ?? "?"}</code> (settings <code>AI_USAGE_USD_INR</code>).
      </p>
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div className="rounded-md border border-border-default bg-bg-primary px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold tabular-nums ${color || ""}`}>{value}</div>
    </div>
  );
}

function PctPill({ value, colorFn }) {
  if (value == null) return "—";
  const cls = colorFn(value);
  return <span className={cls}>{value}%</span>;
}

function marginColor(v) {
  if (v == null) return null;
  if (v >= 90) return "text-emerald-300";
  if (v >= 75) return "text-amber-300";
  return "text-rose-300";
}

function acceptColor(v) {
  if (v == null) return null;
  if (v >= 80) return "text-emerald-300";
  if (v >= 60) return "text-amber-300";
  return "text-rose-300";
}
